/**
 * @file: PlanDetailCard.tsx
 *
 * Plan-detail rows (next-service date, mileage, remark) shared by both
 * maintenance-plan body variants. The subject + abnormal chip used to
 * live here too, but both parents render the subject in their title
 * line already; this card is now just the supplementary key/value
 * rows. The `nextServiceTrailing` slot lets the parent render an
 * inline chip next to the date (e.g. "X 天后到期" on remind).
 */

import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { formatDate } from '@euler/utils/formatters';
import { FC, ReactNode } from 'react';
import { View } from 'react-native';
import { LabelRow, labelColor, valueColor } from './primitives';

/**
 * The few fields this card renders. Both the feed plan-detail snapshot and
 * the denormalized plans list row satisfy it — keeping the card decoupled
 * from any one payload type.
 */
export interface PlanDetailCardDetail {
  customLabel?: string | null;
  nextServiceDate: string | Date;
  nextServiceMileage: number;
  remark?: string | null;
}

export const PlanDetailCard: FC<{
  detail: PlanDetailCardDetail;
  nextServiceTrailing?: ReactNode;
}> = ({ detail, nextServiceTrailing }) => {
  const { rpx } = useResponsiveContext();
  const nextServiceLabel = detail.customLabel ?? '下次保养';
  // Normalize to `YYYY-MM-DD`: feed snapshots arrive as that string already
  // (no-op), the plans row carries a date-only string or Date.
  const nextServiceDate = formatDate(detail.nextServiceDate);
  return (
    <View
      css={`
        margin-top: ${rpx(6)}px;
      `}
    >
      <View
        css={`
          flex-direction: row;
          align-items: center;
          margin-top: ${rpx(4)}px;
        `}
      >
        <Typography.Text
          size={rpx(12)}
          color={labelColor}
          style={{ minWidth: rpx(56) }}
        >
          {nextServiceLabel}
        </Typography.Text>
        <Typography.Text size={rpx(12)} color={valueColor}>
          {nextServiceDate}
        </Typography.Text>
        {nextServiceTrailing ? (
          <View style={{ marginLeft: rpx(8) }}>{nextServiceTrailing}</View>
        ) : null}
      </View>
      {detail.nextServiceMileage ? (
        <LabelRow label="下次里程" value={`${detail.nextServiceMileage} km`} />
      ) : null}
      {detail.remark ? <LabelRow label="备注" value={detail.remark} /> : null}
    </View>
  );
};
